import { useCallback, useEffect, useRef, useState } from "react";
import {
  isPiiScanReady,
  onPiiScanReady,
  redact,
  scanPrompt,
  type DecidedSpan,
  type OverridesSummary,
  type PiiSpan,
} from "@/lib/pii";
import { logger } from "@/lib/logger";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";

/**
 * Send-gate for chat PII sanitation (LAR-142). Wraps the client engine:
 * scan the prompt → if clean, proceed → else open the review dialog → on
 * confirm, redact one-way and proceed with the sanitised text.
 *
 * Fail-closed (plan decision 5): if scanning throws (even after the worker's
 * synchronous fallback), the send is BLOCKED — never sent un-scanned.
 *
 * The whole gate sits behind the PII-Redaction release toggle (business
 * kill-switch): while OFF, `guardSend` proceeds immediately without scanning
 * and `modelReady` reports true so nothing blocks or renders. Fail-closed
 * applies only while the feature is ON.
 */

type ProceedFn = (finalText: string, overrides: OverridesSummary) => void;

const NO_OVERRIDES: OverridesSummary = {
  count: 0,
  types: [],
  hardOverridden: false,
};

interface PendingSend {
  text: string;
  proceed: ProceedFn;
}

export interface UsePiiReviewResult {
  reviewOpen: boolean;
  reviewSpans: PiiSpan[];
  reviewText: string;
  isScanning: boolean;
  /**
   * False while the NER model is still loading (boot preload normally finishes
   * long before the first send — decision 5). Drives the persistent
   * "forbereder sikkerhedstjek…" indicator; sends still work and simply await
   * the model inside `guardSend`.
   */
  modelReady: boolean;
  /** Scan `text`; auto-proceeds if clean, otherwise opens the review dialog. */
  guardSend: (text: string, proceed: ProceedFn) => Promise<void>;
  /** Dialog confirm: redact per the decisions and proceed with the result. */
  confirmReview: (decided: DecidedSpan[]) => void;
  /** Dialog cancel: drop the pending send. */
  cancelReview: () => void;
}

export function usePiiReview(whitelist: string[] = []): UsePiiReviewResult {
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewSpans, setReviewSpans] = useState<PiiSpan[]>([]);
  const [reviewText, setReviewText] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [modelReady, setModelReady] = useState(isPiiScanReady);

  useEffect(() => onPiiScanReady(() => setModelReady(true)), []);
  const pendingRef = useRef<PendingSend | null>(null);
  // Re-entrancy guard: one send is processed at a time. Because the input is no
  // longer cleared synchronously on submit (LAR-27 v2 UI #1), the prompt stays
  // non-empty during the async scan / open review, so without this a fast
  // double-Enter could fire the same send twice or stack review dialogs.
  const inFlightRef = useRef(false);

  const guardSend = useCallback(
    async (text: string, proceed: ProceedFn) => {
      // PII-Redaction flag off: the feature is inert — send directly without
      // scanning (kill-switch; NOT the fail-closed path, which only applies
      // while the feature is on).
      if (!piiFeatureEnabled) {
        proceed(text, NO_OVERRIDES);
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsScanning(true);
      let spans: PiiSpan[];
      try {
        spans = await scanPrompt(text, whitelist);
      } catch (error) {
        // Fail-closed: block the send rather than leak un-scanned PII.
        logger.error("PII scan failed; blocking send", {
          operation: "piiScan",
          error,
        });
        showErrorToast(
          "Kunne ikke tjekke beskeden for personoplysninger. Prøv igen."
        );
        inFlightRef.current = false; // allow a retry
        return;
      } finally {
        setIsScanning(false);
      }

      if (spans.length === 0) {
        proceed(text, NO_OVERRIDES);
        inFlightRef.current = false;
        return;
      }

      pendingRef.current = { text, proceed };
      setReviewText(text);
      setReviewSpans(spans);
      setReviewOpen(true);
      // inFlightRef stays set until confirmReview / cancelReview resolves it.
    },
    [whitelist, piiFeatureEnabled]
  );

  const confirmReview = useCallback((decided: DecidedSpan[]) => {
    const pending = pendingRef.current;
    setReviewOpen(false);
    if (!pending) return;

    const { text: redactedText, overrides } = redact(pending.text, decided);
    // The overrides summary is recorded server-side for admin audit by
    // ConversationHubProvider.sendMessage after the message persists (LAR-27 #5).
    pendingRef.current = null;
    inFlightRef.current = false;
    pending.proceed(redactedText, overrides);
  }, []);

  const cancelReview = useCallback(() => {
    setReviewOpen(false);
    pendingRef.current = null;
    inFlightRef.current = false;
  }, []);

  return {
    reviewOpen,
    reviewSpans,
    reviewText,
    isScanning,
    // Flag off → never block sends or show the "forbereder sikkerhedstjek…"
    // indicator on model readiness.
    modelReady: piiFeatureEnabled ? modelReady : true,
    guardSend,
    confirmReview,
    cancelReview,
  };
}
